// frontend/src/components/Sidebar/Sidebar.jsx

import React, { useMemo } from 'react';
import { Layout, Menu, Button, Dropdown } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import {
    CrownOutlined,
    HomeOutlined,
    WalletOutlined,
    CreditCardOutlined,
    SwapOutlined,
    AimOutlined,
    PlusOutlined,
    RetweetOutlined,
    AccountBookOutlined,
    RocketOutlined,
    TeamOutlined,
    UserAddOutlined,
    FilePdfOutlined,
    ScheduleOutlined,
    ContactsOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    LeftOutlined,
    RightOutlined,
    CalculatorOutlined,
    ShoppingCartOutlined,
    LineChartOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    UserSwitchOutlined,
    DollarCircleOutlined,
} from '@ant-design/icons';
import logoHorizontal from '../../assets/images/logo-horizontal-light.svg';
import logoIcone from '../../assets/images/logo-icon.svg';
import './Sidebar.css';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useAuth } from '../../contexts/AuthContext';

const { Sider } = Layout;

const dashboardMenuItem = {
    key: '/meu-desempenho',
    icon: <HomeOutlined />,
    label: <NavLink to="/meu-desempenho">Meu Desempenho</NavLink>,
};

const financeiroMenuItems = [
    { key: '/relatorios', icon: <FilePdfOutlined />, label: <NavLink to="/relatorios">Relatórios</NavLink> },
    { key: '/contas', icon: <WalletOutlined />, label: <NavLink to="/contas">Contas</NavLink> },
    { key: '/cartoes', icon: <CreditCardOutlined />, label: <NavLink to="/cartoes">Cartões</NavLink> },
    { key: '/transacoes', icon: <SwapOutlined />, label: <NavLink to="/transacoes">Transações</NavLink> },
    { key: '/listas-compras', icon: <ShoppingCartOutlined />, label: <NavLink to="/listas-compras">Listas de compras</NavLink> },
    { key: '/historico-compras', icon: <LineChartOutlined />, label: <NavLink to="/historico-compras">Histórico de compras</NavLink> },
    { key: '/recorrencias', icon: <RetweetOutlined />, label: <NavLink to="/recorrencias">Recorrências</NavLink> },
    { key: '/metas', icon: <AimOutlined />, label: <NavLink to="/metas">Metas</NavLink> },
    { key: '/simuladores', icon: <CalculatorOutlined />, label: <NavLink to="/simuladores">Simuladores</NavLink> },
];

const prestadorMenuItems = [
    { key: '/a-receber', icon: <DollarCircleOutlined />, label: <NavLink to="/a-receber">A receber</NavLink> },
    { key: '/clientes', icon: <TeamOutlined />, label: <NavLink to="/clientes">Clientes</NavLink> },
];

const milhasMenuItems = [
    { key: '/milhas/extrato', icon: <SwapOutlined />, label: <NavLink to="/milhas/extrato">Movimentações</NavLink> },
    { key: '/milhas/clubes', icon: <ScheduleOutlined />, label: <NavLink to="/milhas/clubes">Gestão de Clubes</NavLink> },
    { key: '/milhas/cpfs', icon: <TeamOutlined />, label: <NavLink to="/milhas/cpfs">Controle de CPFs</NavLink> },
    { key: '/milhas/lista-beneficiarios', icon: <UserAddOutlined />, label: <NavLink to="/milhas/lista-beneficiarios">Lista de Beneficiários</NavLink> },
    { key: '/milhas/portadores', icon: <ContactsOutlined />, label: <NavLink to="/milhas/portadores">Gerenciar Portadores</NavLink> },
    { key: '/milhas/programas', icon: <RocketOutlined />, label: <NavLink to="/milhas/programas">Catálogo de Programas</NavLink> },
];

const contaMenuItems = [
    { key: '/meu-plano', icon: <CrownOutlined />, label: <NavLink to="/meu-plano">Meu Plano</NavLink> },
];

const Sidebar = ({
    isOpen,
    onToggle,
    onOpenTransacaoModal,
    onOpenSelecaoCartaoModal,
    onOpenEntradaMilhas,
    onOpenSaidaMilhas,
    onOpenTransfProgramas,
    onOpenTransfPessoas,
    isMobile = false,
}) => {
    const location = useLocation();
    const { isPrivate, togglePrivacy } = usePrivacy();
    const { temModuloFinanceiro, temModuloMilhas, temModoPrestador } = useAuth();

    const selectedMenuKey = location.pathname.startsWith('/simuladores')
        ? '/simuladores'
        : location.pathname.startsWith('/listas-compras')
        ? '/listas-compras'
        : location.pathname.startsWith('/historico-compras')
        ? '/historico-compras'
        : location.pathname;

    const visibleMenuItems = useMemo(() => {
        const items = [];
        if (temModuloFinanceiro || temModuloMilhas) {
            items.push(dashboardMenuItem);
        }
        if (temModuloFinanceiro) {
            const finChildren = temModoPrestador
                ? [...prestadorMenuItems, ...financeiroMenuItems]
                : financeiroMenuItems;
            items.push({ label: 'Financeiro', type: 'group', children: finChildren });
        }
        if (temModuloMilhas) {
            items.push({ label: 'Milhas', type: 'group', children: milhasMenuItems });
        }
        items.push({ label: 'Conta', type: 'group', children: contaMenuItems });
        return items;
    }, [temModuloFinanceiro, temModuloMilhas, temModoPrestador]);

    const actionMenu = (
        <Menu>
            {temModuloFinanceiro && (
                <Menu.ItemGroup key="grupo-financeiro" title="Financeiro">
                    <Menu.Item key="1" icon={<AccountBookOutlined />} onClick={onOpenTransacaoModal}>
                        Lançamento Financeiro
                    </Menu.Item>
                    <Menu.Item key="2" icon={<CreditCardOutlined />} onClick={onOpenSelecaoCartaoModal}>
                        Despesa no Cartão
                    </Menu.Item>
                </Menu.ItemGroup>
            )}
            {temModuloMilhas && (
                <Menu.ItemGroup key="grupo-milhas" title="Milhas">
                    <Menu.Item key="entrada-milhas" icon={<ArrowUpOutlined />} onClick={onOpenEntradaMilhas}>
                        Nova Entrada
                    </Menu.Item>
                    <Menu.Item key="saida-milhas" icon={<ArrowDownOutlined />} onClick={onOpenSaidaMilhas}>
                        Nova Saída
                    </Menu.Item>
                    <Menu.Item key="transf-programas" icon={<SwapOutlined />} onClick={onOpenTransfProgramas}>
                        Transf. Programas
                    </Menu.Item>
                    <Menu.Item key="transf-pessoas" icon={<UserSwitchOutlined />} onClick={onOpenTransfPessoas}>
                        Transf. Pessoas
                    </Menu.Item>
                </Menu.ItemGroup>
            )}
        </Menu>
    );

    const sidebarContent = (
        <>
            <div className={`sidebar-logo-container ${isMobile ? 'mobile' : 'desktop'}`}>
                <img
                    src={(isOpen || isMobile) ? logoHorizontal : logoIcone}
                    alt="Saldo Ativo"
                    className="sidebar-logo"
                />
            </div>

            <div className="sidebar-action-button-container">
                {(temModuloFinanceiro || temModuloMilhas) && (
                <Dropdown overlay={actionMenu} placement="bottom" arrow trigger={['click']}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        className="sidebar-action-button"
                        style={{ width: (isOpen || isMobile) ? '100%' : '40px' }}
                    >
                        {(isOpen || isMobile) && 'Adicionar Lançamento'}
                    </Button>
                </Dropdown>
                )}
            </div>

            <div className="sidebar-menu-container" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[selectedMenuKey]}
                    defaultSelectedKeys={['/meu-desempenho']}
                    items={visibleMenuItems}
                    onClick={isMobile ? onToggle : undefined}
                    style={{ borderRight: 0 }}
                />
            </div>

            <div style={{ padding: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', flexShrink: 0 }}>
                <Button
                    type="text"
                    icon={isPrivate ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={togglePrivacy}
                    block
                    style={{ color: 'rgba(255, 255, 255, 0.65)', textAlign: (isOpen || isMobile) ? 'left' : 'center', paddingLeft: (isOpen || isMobile) ? 12 : 0 }}
                >
                    {(isOpen || isMobile) && (isPrivate ? 'Ocultar Valores' : 'Exibir Valores')}
                </Button>
            </div>
        </>
    );

    if (isMobile) {
        return (
            <div style={{
                minHeight: '100dvh',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'var(--color-shell, #1F4F4F)',
                margin: 0,
                padding: 0,
                width: '100%'
            }}>
                {sidebarContent}
            </div>
        );
    }

    return (
        <Sider
            theme="dark"
            width={250}
            collapsedWidth={80}
            collapsible
            collapsed={!isOpen}
            onCollapse={onToggle}
            trigger={null}
            /* Garante que o trigger nativo seja desativado ao máximo */
            zeroWidthTriggerStyle={{ display: 'none', width: 0, height: 0, overflow: 'hidden' }}
            style={{
                height: '100dvh',
                position: 'sticky',
                top: 0,
                left: 0,
                zIndex: 100,
                display: 'flex',
                flexDirection: 'column'
            }}
            className="custom-sider"
        >
            <div className="sidebar-sider-inner" style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Trigger único: canto superior esquerdo, sutil */}
                {!isMobile && (
                    <div
                        className="sidebar-trigger"
                        onClick={onToggle}
                        title={isOpen ? "Recolher menu" : "Expandir menu"}
                    >
                        {isOpen ? <LeftOutlined /> : <RightOutlined />}
                    </div>
                )}
                {sidebarContent}
            </div>
        </Sider>
    );
};

export default Sidebar;